import { Client, ClientKpiValue } from "@acto/gql/graphql";
import OfferSummary, { type OfferSummaryInput } from "./OfferSummary";
import OrderSummary, { type OrderSummaryInput } from "./OrderSummary";
import type Observation from "./Observation";
import ClientTag from "./ClientTag";
import LocalizedStringMap from "./LocalizedStringMap";

/**
 * Client shape consumed by {@link Customer.fromGraphql}. Matches the generated `Client`
 * except `lastOrder`, which is a narrow summary projection rather than a full `Order`, and
 * the KPI values, whose generated type demands a full `dateRanges` that partial selections never carry.
 * The full `Order` type requires `lineItems` and `status`, which the client fragments
 * deliberately never select.
 */
export type ClientKpiValueInput = Pick<ClientKpiValue, "totalValue" | "previousTotalValue" | "percentageChange">;

export type ClientForCustomer = Omit<
  Client,
  "lastOrder" | "lastOffer" | "revenue" | "profit" | "orderCount" | "margin"
> & {
  lastOrder?: OrderSummaryInput | null;
  lastOffer?: OfferSummaryInput | null;
  revenue?: ClientKpiValueInput | null;
  profit?: ClientKpiValueInput | null;
  orderCount?: ClientKpiValueInput | null;
  margin?: ClientKpiValueInput | null;
};

export interface MatchReason {
  key: string;
  value: string;
}

export interface CustomerKpiValue {
  totalValue: number;
  previousTotalValue: number;
  percentageChange: number | null;
}

const toCustomerKpiValue = (value: ClientKpiValueInput): CustomerKpiValue => ({
  totalValue: value.totalValue,
  previousTotalValue: value.previousTotalValue,
  percentageChange: value.percentageChange ?? null,
});

export default class Customer {
  private _observation?: Observation;
  private _revenueKpi?: CustomerKpiValue;
  private _profitKpi?: CustomerKpiValue;
  private _ordersKpi?: CustomerKpiValue;
  private _marginKpi?: CustomerKpiValue;

  constructor(
    public readonly id: string,
    public readonly externalReference: string,
    public readonly name: string,
    public readonly legacyId?: string,
    public readonly domains?: string[],
    public readonly region?: string,
    public readonly branch?: string,
    public readonly group?: string,
    public readonly street?: string,
    public readonly postcode?: string,
    public readonly city?: string,
    public readonly country?: string,
    public readonly industry?: string,
    public readonly lastVisitDate?: string,
    public readonly lastOrder?: OrderSummary,
    public readonly lastOffer?: OfferSummary,
    public readonly matchReasons?: MatchReason[],
    public readonly clientTags?: ClientTag[],
    public readonly assignedEmployee?: { id: string; name: string },
    public readonly orderYears: number[] = [],
    public readonly offerYears: number[] = [],
    public readonly noteYears: number[] = [],
    private readonly shortStory?: LocalizedStringMap,
  ) {}

  public getLocalizedShortStory(locale: string): string | undefined {
    return this.shortStory?.hasTranslations() ? this.shortStory.getTranslation(locale) : undefined;
  }

  static fromGraphql(data: ClientForCustomer | Customer): Customer {
    // If already transformed, return as-is
    if (data instanceof Customer) {
      return data;
    }

    const lastVisitDate = data.lastVisitDate ?? undefined;
    const lastOrder = OrderSummary.fromGraphql(data.lastOrder ?? undefined);
    const lastOffer = OfferSummary.fromGraphql(data.lastOffer ?? undefined);
    const matchReasons = data.matchReasons?.map((reason) => ({
      key: reason.key,
      value: reason.value,
    }));
    const clientTags = data.clientTags?.map(ClientTag.fromGraphql);

    const assignedEmployee = data.assignedEmployee
      ? { id: data.assignedEmployee.id, name: data.assignedEmployee.name }
      : undefined;

    const customer = new Customer(
      data.id,
      data.externalReference,
      data.name || data.externalReference,
      data.legacyId ?? undefined,
      data.domains ?? undefined,
      data.region ?? undefined,
      data.branch ?? undefined,
      data.group ?? undefined,
      data.street ?? undefined,
      data.postcode ?? undefined,
      data.city ?? undefined,
      data.country ?? undefined,
      data.industry ?? undefined,
      lastVisitDate,
      lastOrder,
      lastOffer,
      matchReasons,
      clientTags,
      assignedEmployee,
      data.orderYears ?? [],
      data.offerYears ?? [],
      data.noteYears ?? [],
      data.shortStory ? LocalizedStringMap.fromGraphql(data.shortStory) : undefined,
    );

    if (data.revenue) customer.setRevenueKpi(toCustomerKpiValue(data.revenue));
    if (data.profit) customer.setProfitKpi(toCustomerKpiValue(data.profit));
    if (data.orderCount) customer.setOrdersKpi(toCustomerKpiValue(data.orderCount));
    if (data.margin) customer.setMarginKpi(toCustomerKpiValue(data.margin));

    return customer;
  }

  get observation(): Observation | undefined {
    return this._observation;
  }

  setObservation(observation: Observation): void {
    this._observation = observation;
  }

  get revenueKpi(): CustomerKpiValue | undefined {
    return this._revenueKpi;
  }

  setRevenueKpi(value: CustomerKpiValue | undefined): void {
    this._revenueKpi = value;
  }

  get profitKpi(): CustomerKpiValue | undefined {
    return this._profitKpi;
  }

  setProfitKpi(value: CustomerKpiValue | undefined): void {
    this._profitKpi = value;
  }

  get ordersKpi(): CustomerKpiValue | undefined {
    return this._ordersKpi;
  }

  setOrdersKpi(value: CustomerKpiValue | undefined): void {
    this._ordersKpi = value;
  }

  get marginKpi(): CustomerKpiValue | undefined {
    return this._marginKpi;
  }

  setMarginKpi(value: CustomerKpiValue | undefined): void {
    this._marginKpi = value;
  }
}
